import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "About", id: "about" },
  { label: "The Journey", id: "journey" },
  { label: "Our Work", id: "gallery" },
  { label: "Stories", id: "stories" },
];

function MailGlyph({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6h16v12H4z" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  );
}

function BrandLogo({ onNavigate }) {
  return (
    <a
      href="#hero"
      onClick={(e) => onNavigate(e, "hero")}
      data-testid="nav-logo"
      className="flex items-center gap-3 md:gap-4 group shrink-0"
    >
      <img
        src="/logo.png"
        alt="Gifting Concierge"
        className="h-14 w-14 md:h-16 md:w-16 object-contain"
      />
      <span className="hidden sm:block leading-tight">
        <span className="block font-serif-display text-2xl md:text-[26px] text-[#2A1D15] tracking-tight">
          Gifting Concierge
        </span>
        <span className="block overline text-[10px] text-[#8B4513] mt-1">
          Wrapped with Love
        </span>
      </span>
    </a>
  );
}

function DesktopLinks({ onNavigate }) {
  return (
    <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
      {links.map((l) => (
        <a
          key={l.id}
          href={`#${l.id}`}
          data-testid={`nav-link-${l.id}`}
          onClick={(e) => onNavigate(e, l.id)}
          className="overline text-[#2A1D15] hover:text-[#8B4513] transition-colors duration-300"
        >
          {l.label}
        </a>
      ))}
    </nav>
  );
}

function MobileMenu({ onNavigate }) {
  return (
    <div
      data-testid="nav-mobile-menu"
      className="lg:hidden bg-[#ebe0cd] border-t border-[#8B4513]/10"
    >
      <div className="px-6 py-7 flex flex-col gap-5">
        {links.map((l) => (
          <a
            key={l.id}
            href={`#${l.id}`}
            onClick={(e) => onNavigate(e, l.id)}
            data-testid={`nav-mobile-link-${l.id}`}
            className="overline text-[#2A1D15] hover:text-[#8B4513]"
          >
            {l.label}
          </a>
        ))}
        <a
          href="#contact"
          onClick={(e) => onNavigate(e, "contact")}
          data-testid="nav-mobile-contact"
          className="inline-flex items-center justify-center gap-3 bg-[#8B4513] text-[#FDFBF7] px-6 py-3 rounded-full"
        >
          <MailGlyph className="w-4 h-4" />
          <span className="overline text-[11px]">Contact us</span>
        </a>
      </div>
    </div>
  );
}

function ContactCta({ onNavigate }) {
  return (
    <a
      href="#contact"
      onClick={(e) => onNavigate(e, "contact")}
      data-testid="nav-contact-cta"
      className="hidden md:inline-flex items-center gap-3 bg-[#8B4513] text-[#FDFBF7] pl-2 pr-6 py-2 rounded-full hover:bg-[#2A1D15] transition-colors duration-300 shrink-0"
    >
      <span className="w-9 h-9 rounded-full bg-[#FDFBF7] text-[#8B4513] flex items-center justify-center">
        <MailGlyph className="w-5 h-5" />
      </span>
      <span className="overline text-[11px] tracking-[0.22em]">Contact us</span>
    </a>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#ebe0cd]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1340px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24 py-5 md:py-6 flex items-center justify-between gap-6">
        <BrandLogo onNavigate={handleClick} />
        <DesktopLinks onNavigate={handleClick} />
        <ContactCta onNavigate={handleClick} />

        <button
          data-testid="nav-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#2A1D15] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && <MobileMenu onNavigate={handleClick} />}
    </header>
  );
}
